import { STORES_METHODS_MAP, STORE_MAP } from "@/config/cache/cache-engine"
import { DATA_MAP, DATA_DISTRIBUTION_SCHEMA } from "@/config/cache/cache-service"
import { clearObjectAttrs } from '@/helpers/utilsHelper'
import cacheEngineHelper from "@/helpers/cacheEngineHelper"

var cacheEngine = await cacheEngineHelper()

/**
 * Description
 * @param {string} key Data key name in store
 * @param {object} dataDistributionSchema Data distribution schema
 * @returns {object} Data distribution schema
 */
function _getDistributionSchema(key, dataDistributionSchema = null) {
    const schema = dataDistributionSchema ?? DATA_DISTRIBUTION_SCHEMA?.[key]

    return schema
}

/**
 * Description
 * @param {string} key Data key name in store
 * @param {any} data Raw data
 * @param {object} dataDistributionSchema Data distribution schema
 * @returns {Array} Array of entries [storeName, transformedData]
 */
function _distributeData(key, data, dataDistributionSchema) {
    const distributionSchema = _getDistributionSchema(key, dataDistributionSchema)

    if (!distributionSchema) return undefined

    const distributedData = Object.entries(distributionSchema)
        .map(([storeName, schema]) => {
            const { attrs, exclude } = schema
            let transformedData = data

            if (!attrs || !attrs.length) {
                return [
                    storeName,
                    ...[Array.isArray(transformedData) ? transformedData.map(item => clearObjectAttrs((exclude || []), item)) : clearObjectAttrs((exclude || []), transformedData)]
                ]
            }

            transformedData = transformedData
                .map((item) => attrs.reduce((tranformingObject, schemaKey) => {
                    tranformingObject[schemaKey] = item[schemaKey]
                    return tranformingObject
                }, {}))
                .map((item) => clearObjectAttrs((exclude || []), item))

            // TODO options usage

            return [storeName, transformedData]
        })

    return distributedData
}

/**
 * Description
 * @param {any} key Data key name in store
 * @param {any} version Version from backend
 * @returns {boolean} Result of versions comparing
 */
async function _validateKeyVersion(key, version) {
    const cacheVersion = await cacheEngine[STORES_METHODS_MAP[STORE_MAP.VERSION].GET](key)

    return version === cacheVersion
}

/**
 * Description
 * @param {Array} versionEntries Array of entries [dataKey, version]
 * @returns {Promise} Request in indexDB promise
 */
function _updateVersion(versionEntries) {
    const requests = versionEntries.map(([key, version]) => {
        cacheEngine[STORES_METHODS_MAP[STORE_MAP.VERSION].SET](key, version)
    })

    return Promise.all(requests)
}

function _getVersions(key) {
    return cacheEngine[STORES_METHODS_MAP[STORE_MAP.VERSION].GET](key)
}

const CURRENT_VERSIONS_KEY = 'currentVersions'
const generateCurrentVersionsKey = (uniqKey) => `${CURRENT_VERSIONS_KEY}--${uniqKey}`

/**
 * Description
 * @param {string | number} uniqKey Uniq key for current versions in context of company
 * @param {object} versions Version of data from backend
 */
function _updateCurrentVersions(uniqKey, versions) {
    const currentVersionsKey = generateCurrentVersionsKey(uniqKey)

    return cacheEngine[STORES_METHODS_MAP[STORE_MAP.VERSION].SET](currentVersionsKey, versions)
}

/**
 * Description
 * @param {string | number} uniqKey Uniq key for current versions in context of company
 */
function _getCurrentVersions(uniqKey) {
    const currentVersionsKey = generateCurrentVersionsKey(uniqKey)

    return cacheEngine[STORES_METHODS_MAP[STORE_MAP.VERSION].GET](currentVersionsKey)
}

/**
 * Description
 * @param {string} key Data key name in store
 * @param {string} version Version of data from backend
 * @param {string} storage Store name
 * @returns {Promise} Request in indexDB promise
 */
async function _cacheGetRequestWrap(key, version, storage) {
    const isCacheValid = await _validateKeyVersion(key, version)

    if (!isCacheValid) {
        return Promise.reject({
            message: `Cache unable to return ${key} by version ${version}.`
        })
    }

    const cacheData = await cacheEngine[STORES_METHODS_MAP[storage].GET](key)

    if (!cacheData) {
        return Promise.reject({
            message: `Cache unable to return ${key} by version ${version} cause it is ${typeof cacheData}.`
        })
    }

    return cacheData
}

/**
 * Description
 * @param {string} key Data key name in store
 * @param {any} data Raw data
 * @param {string} version Version of data from backend
 * @param {object} dataDistributionSchema В новой реализации получении данных мы в самом модуле прописываем схему хранения данных в кеше
 * @returns {Promise} Request in indexDB promise
 */
function _cacheSetRequestWrap(key, data, version, dataDistributionSchema) {
    const entityKey = key.split('--')[0]
    const distributedData = _distributeData(entityKey, data, dataDistributionSchema)
    if (!distributedData) {
        Promise.reject({
            message: `Cache service does not have distribution schema for this key OR key is not registered. Key: ${key}`
        })
    }

    const requestsPromises = [
        ...distributedData.map(([storeName, cachebleData]) => {
            return cacheEngine[STORES_METHODS_MAP[storeName].SET](key, cachebleData)
        }),
        cacheEngine[STORES_METHODS_MAP[STORE_MAP.VERSION].SET](key, version)
    ]

    return Promise.all(requestsPromises)
}

export default function() {
    function updateVersion(versionEntries) {
        return _updateVersion(versionEntries)
    }

    function getVersion(key) {
        return _getVersions(key)
    }

    return {
        updateVersion,
        getVersion,

        cacheSetRequestWrap: _cacheSetRequestWrap,
        cacheGetRequestWrap: _cacheGetRequestWrap,

        updateCurrentVersions: _updateCurrentVersions,
        getCurrentVersions: _getCurrentVersions
    }
}
